import { useRef, useCallback } from "react";

import {
  UseVideoPlaybackControlOptions,
  UseVideoPlaybackControlReturn,
} from "./types";

import { clamp } from "@/utils/MathHelper";

/**
 * Custom hook for controlling video playback state
 * Manages pausing, resuming, and seeking video playback
 *
 * @param options - Configuration options
 * @returns Object containing playback control functions
 *
 * @example
 * ```tsx
 * const { wasPlaying, pauseAndStore, resumeIfWasPlaying, seekTo } = useVideoPlaybackControl({
 *   videoRef,
 *   onPlay: () => console.log('Playing'),
 *   onPause: () => console.log('Paused'),
 * });
 *
 * // Pause video and remember state
 * pauseAndStore();
 *
 * // Resume if it was playing
 * resumeIfWasPlaying();
 *
 * // Seek to 30 seconds
 * seekTo(30);
 * ```
 */
const useVideoPlaybackControl = ({
  videoRef,
  onPlay,
  onPause,
}: UseVideoPlaybackControlOptions): UseVideoPlaybackControlReturn => {
  const wasPlayingRef = useRef<boolean>(false);

  /**
   * Pause the video and store whether it was playing
   * Only stores the state if video is currently playing (not already paused)
   * This prevents overwriting the stored state on subsequent calls
   */
  const pauseAndStore = useCallback(() => {
    if (videoRef?.current) {
      const isCurrentlyPlaying = !videoRef.current.paused;

      // Only update wasPlayingRef if video is currently playing
      // This prevents a second call from overwriting the stored state
      if (isCurrentlyPlaying) {
        wasPlayingRef.current = true;
      }

      videoRef.current.pause();
      onPause?.();
    }
  }, [videoRef, onPause]);

  /**
   * Resume playback if the video was playing before
   * Resets the wasPlayingRef after resuming
   */
  const resumeIfWasPlaying = useCallback(() => {
    if (videoRef?.current && wasPlayingRef.current) {
      videoRef.current.play();
      onPlay?.();
    }
    // Reset the flag after attempting to resume
    wasPlayingRef.current = false;
  }, [videoRef, onPlay]);

  /**
   * Seek to a specific time in the video
   */
  const seekTo = useCallback(
    (time: number) => {
      if (videoRef?.current) {
        const duration = videoRef.current.duration || 0;
        videoRef.current.currentTime = clamp(time, 0, duration);
      }
    },
    [videoRef],
  );

  return {
    wasPlaying: wasPlayingRef.current,
    pauseAndStore,
    resumeIfWasPlaying,
    seekTo,
  };
};

export { useVideoPlaybackControl };
